import { useCallback } from "react";

/**
 * Android 브리지와의 통신을 위한 공통 hook
 *
 * @returns {Object} Android 브리지 관련 함수들
 */
export const useAndroidBridge = () => {
  /**
   * Android 네이티브로 데이터를 전송하는 함수
   *
   * @param type - 전송할 이벤트 타입
   * @param payload - 전송할 데이터
   * @returns {boolean} 전송 성공 여부
   */
  const androidInterface = (window as any).AndroidInterface;

  const sendToAndroid = useCallback((type: string, payload: any): boolean => {
    try {
      const payloadStr = JSON.stringify(payload);

      // AndroidInterface 존재 여부 확인

      if (androidInterface && typeof androidInterface.onData === "function") {
        console.log("📱 [useAndroidBridge] sendToAndroid", type, payloadStr);
        androidInterface.onData(type, payloadStr);
        return true;
      } else {
        console.log("⚠️ [useAndroidBridge] androidInterface is not defined");
        return false;
      }
    } catch (error) {
      console.error("❌ [useAndroidBridge] sendToAndroid error:", error);
      return false;
    }
  }, []);

  const bridgeLogout = () => {
    console.log("[logout] 네이티브 로그아웃 호출");

    try {
      if (androidInterface && typeof androidInterface.logout === "function") {
        console.log("[logout] androidInterface.logout exists, calling...");

        try {
          androidInterface.logout();
          console.log("[logout] androidInterface.logout called successfully");
          sendToAndroid("go-to-page", "login");
        } catch (callError) {
          console.warn(
            "[logout] AndroidInterface.logout() 호출 실패:",
            callError
          );
          // 호출 실패 시에도 계속 진행
        }
      } else {
        console.warn(
          "[logout] androidInterface.logout is not defined - 웹 전용 로그아웃으로 진행"
        );
        // AndroidInterface가 없는 경우 웹 전용 로그아웃 처리
        try {
          // 최후 수단으로 localStorage 정리
          localStorage.removeItem("accessToken");
          localStorage.removeItem("membership_access_token");
          localStorage.removeItem("userInfo");
          console.log("[logout] localStorage 정리 완료");
          sendToAndroid("go-to-page", "login");
        } catch (fallbackError) {
          console.warn("[logout] 폴백 로그아웃 처리 실패:", fallbackError);
        }
      }
    } catch (error) {
      console.error("[logout] 로그아웃 처리 중 예상치 못한 오류:", error);
    }
  };

  /**
   * AndroidInterface가 사용 가능한지 확인하는 함수
   *
   * @returns {boolean} AndroidInterface 사용 가능 여부
   */
  const isAndroidInterfaceAvailable = useCallback((): boolean => {
    const androidInterface = (window as any).AndroidInterface;
    return !!(
      androidInterface && typeof androidInterface.onData === "function"
    );
  }, []);

  /**
   * Android 환경인지 확인하는 함수
   *
   * @returns {boolean} Android 환경 여부
   */
  const isAndroidEnvironment = useCallback((): boolean => {
    return (
      typeof window !== "undefined" &&
      typeof (window as any).AndroidInterface !== "undefined"
    );
  }, []);

  /**
   * 특정 메서드가 AndroidInterface에 존재하는지 확인하는 함수
   *
   * @param methodName - 확인할 메서드명
   * @returns {boolean} 메서드 존재 여부
   */
  const isMethodAvailable = useCallback((methodName: string): boolean => {
    const androidInterface = (window as any).AndroidInterface;
    return !!(
      androidInterface && typeof androidInterface[methodName] === "function"
    );
  }, []);

  /**
   * AndroidInterface의 특정 메서드를 호출하는 함수
   *
   * @param methodName - 호출할 메서드명
   * @param args - 메서드 인자들
   * @returns {any} 메서드 호출 결과
   */
  const callAndroidMethod = useCallback(
    (methodName: string, ...args: any[]): any => {
      try {
        const androidInterface = (window as any).AndroidInterface;

        if (
          androidInterface &&
          typeof androidInterface[methodName] === "function"
        ) {
          console.log(
            `📱 [useAndroidBridge] callAndroidMethod: ${methodName}`,
            args
          );
          return androidInterface[methodName](...args);
        } else {
          console.warn(
            `⚠️ [useAndroidBridge] Method ${methodName} is not available`
          );
          return null;
        }
      } catch (error) {
        console.error(
          `❌ [useAndroidBridge] callAndroidMethod error (${methodName}):`,
          error
        );
        return null;
      }
    },
    []
  );

  /**
   * 주소 선택 팝업을 닫는 함수 (Android 네이티브 호출)
   */
  const closePostcodePopup = useCallback((): boolean => {
    try {
      const androidInterface = (window as any).AndroidInterface;

      if (
        androidInterface &&
        typeof androidInterface.closePostcodePopup === "function"
      ) {
        console.log("📱 [useAndroidBridge] closePostcodePopup 호출");
        androidInterface.closePostcodePopup();
        return true;
      } else {
        console.warn(
          "⚠️ [useAndroidBridge] closePostcodePopup 메서드가 없습니다"
        );
        return false;
      }
    } catch (error) {
      console.error("❌ [useAndroidBridge] closePostcodePopup error:", error);
      return false;
    }
  }, []);

  return {
    sendToAndroid,
    bridgeLogout,
    isAndroidInterfaceAvailable,
    isAndroidEnvironment,
    isMethodAvailable,
    callAndroidMethod,
    closePostcodePopup,
  };
};

export default useAndroidBridge;
